// services/branchApi.ts
// Branch-portal API client. Uses its OWN token (localStorage 'branchToken')
// so a branch-manager session is independent of the admin staff session.
const BASE = 'https://api.playbro.com';

export const BRANCH_TOKEN_KEY = 'branchToken';
export const BRANCH_USER_KEY = 'branchUser';

export function getBranchToken() { return localStorage.getItem(BRANCH_TOKEN_KEY); }
export function getBranchUser() {
  try { return JSON.parse(localStorage.getItem(BRANCH_USER_KEY) || 'null'); } catch { return null; }
}
export function branchLogout() {
  localStorage.removeItem(BRANCH_TOKEN_KEY);
  localStorage.removeItem(BRANCH_USER_KEY);
}

async function branchFetch<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getBranchToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers as any),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 401 || res.status === 403) {
    branchLogout();
    if (!window.location.pathname.startsWith('/branch/login')) window.location.href = '/branch/login';
    throw new Error('Session expired');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.message || `Request failed (${res.status})`);
  }
  return res.json();
}

// ─── auth ───────────────────────────────────────────────────────
export async function branchLogin(identifier: string, password: string) {
  const res = await fetch(`${BASE}/api/branch/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, password }),
  });
  const data = await res.json();
  if (!res.ok || data.status !== 'success') throw new Error(data.message || 'Login failed');
  localStorage.setItem(BRANCH_TOKEN_KEY, data.token);
  localStorage.setItem(BRANCH_USER_KEY, JSON.stringify(data.user));
  return data.user;
}

// multipart helper for proof uploads (does NOT set Content-Type — the browser
// sets the multipart boundary).
async function branchUpload<T = any>(path: string, form: FormData, method = 'PUT'): Promise<T> {
  const token = getBranchToken();
  const res = await fetch(`${BASE}${path}`, {
    method, body: form,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.status === 401 || res.status === 403) {
    if (res.status === 401) { branchLogout(); if (!window.location.pathname.startsWith('/branch/login')) window.location.href = '/branch/login'; }
  }
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || err.message || `Request failed (${res.status})`); }
  return res.json();
}

// Fetch a proof screenshot (auth-protected) as an object URL for <img>/new tab.
// kind: deposits 'screenshot' | withdrawals 'screenshot' (user proof) | 'payout-proof' (agent proof)
export async function branchProofUrl(kind: 'deposits' | 'withdrawals', id: number | string, variant: 'screenshot' | 'payout-proof' = 'screenshot') {
  const token = getBranchToken();
  const res = await fetch(`${BASE}/api/branch/${kind}/${id}/${variant}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Proof not available');
  return URL.createObjectURL(await res.blob());
}

// ─── dashboard ──────────────────────────────────────────────────
export const branchDashboard = () => branchFetch('/api/branch/dashboard');

// Shared filter shape for the deposit/withdraw lists + reports.
export interface TxnFilters {
  status?: string; page?: number; limit?: number;
  from?: string; to?: string; agentId?: number | string;
  method?: string; bank?: string; search?: string;
}
const txnQs = (params: TxnFilters = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
  return qs.toString();
};

// ─── deposits ───────────────────────────────────────────────────
export const branchDeposits = (params: TxnFilters = {}) =>
  branchFetch(`/api/branch/deposits?${txnQs(params)}`);
export const branchApproveDeposit = (id: string, remark: string) =>
  branchFetch(`/api/branch/deposits/${id}/approve`, { method: 'PUT', body: JSON.stringify({ remark }) });
export const branchRejectDeposit = (id: string, remark: string) =>
  branchFetch(`/api/branch/deposits/${id}/reject`, { method: 'PUT', body: JSON.stringify({ remark }) });

// ─── withdrawals ────────────────────────────────────────────────
export const branchWithdrawals = (params: TxnFilters = {}) =>
  branchFetch(`/api/branch/withdrawals?${txnQs(params)}`);

// ─── report CSV download (deposits | withdrawals), honours all filters ──
export async function downloadBranchReport(kind: 'deposits' | 'withdrawals' | 'auto-withdrawals', params: TxnFilters = {}) {
  const token = getBranchToken();
  const res = await fetch(`${BASE}/api/branch/reports/${kind}.csv?${txnQs(params)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Report download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `branch_${kind}_report.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
// Done requires a payout-proof image; Rejected just needs a remark. Always
// multipart so the optional file rides along.
export const branchUpdateWithdraw = (id: number, status: 'Done' | 'Rejected', remark: string, proof?: File | null) => {
  const form = new FormData();
  form.append('status', status);
  form.append('remark', remark);
  if (proof) form.append('proof', proof);
  return branchUpload(`/api/branch/withdrawals/${id}/status`, form, 'PUT');
};

// ─── automatic (crypto) withdrawals ─────────────────────────────
// One queue over CCPayment + NOWPayments. Requests are routed to agents by the
// INR equivalent of the crypto amount; approving fires the gateway payout.
export type AutoProvider = 'ccpayment' | 'nowpayments';
export type AutoState = 'pending' | 'processing' | 'success' | 'failed' | 'rejected';

export interface AutoWithdrawRow {
  provider: AutoProvider;
  id: number;
  user_id: number;
  username: string;
  currency: string;
  chain: string | null;
  address: string;
  amount: number;
  amount_inr: number | null;   // null = no exchange rate on file for this coin
  created_at: string | null;
  tx: string | null;
  remark: string | null;
  raw_status: string;
  state: AutoState;
  action_by: string | null;
  handled_at: string | null;
}

export interface AutoFilters {
  state?: string; provider?: string; page?: number; limit?: number;
  from?: string; to?: string; agentId?: number | string; search?: string;
}

export const branchAutoWithdrawals = (
  params: AutoFilters = {},
): Promise<{ data: AutoWithdrawRow[]; meta: { total: number; page: number; limit: number; inrUsdRate: number } }> =>
  branchFetch(`/api/branch/auto-withdrawals?${txnQs(params as TxnFilters)}`);

// Both actions move money irreversibly, so both need the caller's own 6-digit
// authenticator code on top of their session.
export const branchApproveAutoWithdraw = (provider: AutoProvider, id: number, remark: string, code: string) =>
  branchFetch('/api/branch/auto-withdrawals/approve', {
    method: 'POST', body: JSON.stringify({ provider, id, remark, code }),
  });

export const branchRejectAutoWithdraw = (provider: AutoProvider, id: number, remark: string, code: string) =>
  branchFetch('/api/branch/auto-withdrawals/reject', {
    method: 'POST', body: JSON.stringify({ provider, id, remark, code }),
  });

// ─── the COMPANY authenticator (the Super Admin's key) ──────────────
// Read-only. Branch staff never enrol or manage a key; they type the code from
// the Super Admin's authenticator, which is enrolled in the admin panel only.
export interface BranchTwoFaStatus { isEnabled: boolean }
export const branchTwoFaStatus = (): Promise<BranchTwoFaStatus> => branchFetch('/api/branch/2fa/status');

// ─── branch staff (agents + executives) ─────────────────────────
export interface BranchStaff {
  id: number; username: string; email: string; status: string;
  role: 'agent' | 'executive'; parentId: number | null; parentName: string | null;
  rangeMin: number; rangeMax: number; canDeposit: boolean; canWithdraw: boolean;
  tabs: string[]; createdAt: string;
}
export const branchStaff = (): Promise<{ data: BranchStaff[] }> => branchFetch('/api/branch/staff');
export const branchCreateAgent = (payload: { username: string; email?: string; password: string; rangeMin: number; rangeMax: number; canDeposit: boolean; canWithdraw: boolean }) =>
  branchFetch('/api/branch/agents', { method: 'POST', body: JSON.stringify(payload) });
export const branchCreateExecutive = (payload: { username: string; email?: string; password: string; tabs: string[] }) =>
  branchFetch('/api/branch/executives', { method: 'POST', body: JSON.stringify(payload) });
export const branchUpdateStaff = (id: number, payload: any) =>
  branchFetch(`/api/branch/staff/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const branchLockStaff = (id: number, locked: boolean) =>
  branchFetch(`/api/branch/staff/${id}/lock`, { method: 'PATCH', body: JSON.stringify({ locked }) });
export const branchResetStaffPassword = (id: number, newPassword: string) =>
  branchFetch(`/api/branch/staff/${id}/password`, { method: 'PATCH', body: JSON.stringify({ newPassword }) });

// ─── bank accounts: tier-pool toggle (existing) + branch-owned CRUD ──
export const branchBankAccounts = () => branchFetch('/api/branch/banking/accounts');
export const branchToggleBankAccount = (bankAccountId: number, enabled: boolean) =>
  branchFetch('/api/branch/banking/accounts/toggle', {
    method: 'POST', body: JSON.stringify({ bankAccountId, enabled }),
  });

export const branchOwnedAccounts = () => branchFetch('/api/branch/banking/owned-accounts');
export const branchCreateOwnedAccount = (payload: any, qr?: File | null) => {
  const form = new FormData();
  Object.entries(payload).forEach(([k, v]) => v != null && form.append(k, String(v)));
  if (qr) form.append('qr_image', qr);
  return branchUpload('/api/branch/banking/owned-accounts', form, 'POST');
};
export const branchUpdateOwnedAccount = (id: number, payload: any, qr?: File | null) => {
  const form = new FormData();
  Object.entries(payload).forEach(([k, v]) => v != null && form.append(k, String(v)));
  if (qr) form.append('qr_image', qr);
  return branchUpload(`/api/branch/banking/owned-accounts/${id}`, form, 'PUT');
};
export const branchSetOwnedAccountStatus = (id: number, status: string) =>
  branchFetch(`/api/branch/banking/owned-accounts/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
export const branchDeleteOwnedAccount = (id: number) =>
  branchFetch(`/api/branch/banking/owned-accounts/${id}`, { method: 'DELETE' });

// ─── backward-compatible aliases ────────────────────────────────
export const branchExecutives = branchStaff;
export const branchLockExecutive = branchLockStaff;
export const branchResetExecutivePassword = branchResetStaffPassword;

// ─── reports ────────────────────────────────────────────────────
export const branchProfitLoss = (params: { from?: string; to?: string } = {}) => {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  return branchFetch(`/api/branch/reports/profit-loss?${qs.toString()}`);
};
export const branchActionLogs = () => branchFetch('/api/branch/reports/action-logs');
